import type { CommandHandler } from '../types';

const MANAGER_FIELD_NAMES = ['Manager', 'ManagerCrmRelationId'];

const getRowValue = (row: Record<string, unknown>, fieldName: string): unknown =>
  row[fieldName] ??
  Object.entries(row).find(([key]) => key.toLowerCase() === fieldName.toLowerCase())?.[1];

const getPositiveInteger = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export const changeEmployeeManagerCommandHandler: CommandHandler = (command, context) => {
  const selectedIds = Array.from(context.selectedRows ?? []);
  if (selectedIds.length === 0) {
    return { success: false, error: 'Please select at least one employee.' };
  }

  const rows = context.gridData.length > 0 ? context.gridData : context.displayData;
  const selectedRows = selectedIds
    .map((selectedId) =>
      rows.find((row) => {
        const rowId = getPositiveInteger(row.CrmRelationId ?? row.Id);
        return rowId != null && String(rowId) === String(selectedId);
      })
    )
    .filter((row): row is Record<string, unknown> => row != null);
  const employeeIds = selectedIds
    .map((selectedId) => {
      const row = rows.find((candidate) => {
        const rowId = getPositiveInteger(candidate.CrmRelationId ?? candidate.Id);
        return rowId != null && String(rowId) === String(selectedId);
      });
      return getPositiveInteger(row?.CrmRelationId ?? row?.Id ?? selectedId);
    })
    .filter((id): id is number => id != null);

  if (employeeIds.length === 0) {
    return { success: false, error: 'No valid employees selected.' };
  }

  const managerField = context.columns.find((column) => {
    const dataField = column.dataField ?? column.field;
    return MANAGER_FIELD_NAMES.some((name) => dataField.toLowerCase() === name.toLowerCase());
  });

  if (!managerField?.key) {
    return {
      success: false,
      error: 'Manager field is not configured for this grid.',
    };
  }

  if (!context.openEmployeeManagerPicker) {
    return { success: false, error: 'Change manager dialog is not available.' };
  }

  const managerFieldName = managerField.dataField ?? managerField.field;
  const currentManagerIds = selectedRows
    .map((row) => getPositiveInteger(getRowValue(row, managerFieldName)))
    .filter((id): id is number => id != null);

  context.openEmployeeManagerPicker(
    command,
    [...new Set(employeeIds)],
    managerFieldName,
    managerField.key,
    [...new Set(currentManagerIds)],
    managerField.defaultFilters
  );

  return { success: true };
};
